import type { TypedDocumentNode } from '@apollo/client';
import type { ResultOf } from '@newfront-insurance/graphql-frontend';
import { graphql } from '@newfront-insurance/graphql-frontend';

export type ChatAppType = 'OUTLOOK' | 'PPT' | 'NAVIGATOR';
export type ChatLifecycleStatus = 'ACTIVE' | 'ARCHIVED';

export type GetChatsByFiltersChat = {
  uuid?: string | null;
  userUuid: string;
  accountUuid?: string | null;
  account?: {
    uuid?: string | null;
    name: string;
  } | null;
  appType: ChatAppType;
  status: ChatLifecycleStatus;
  title: string;
  summary: string;
  createdAt?: string | null;
  updatedAt?: string | null;
  deletedAt?: string | null;
  lastMessageAt?: string | null;
  hasUnreadMessages: boolean;
  activeStreamId?: string | null;
};

export type GetChatsByFilters = GetChatsByFiltersChat[];

type GetChatsByFiltersData = {
  chatsList: GetChatsByFilters;
};

type GetChatsByFiltersVariables = {
  userUuid?: string | null;
  accountUuid?: string | null;
  appType?: ChatAppType | null;
  statusFilter?: ChatLifecycleStatus | null;
};

export const GET_CHATS_BY_FILTERS = graphql(`
  query GetChatsByFilters($userUuid: String, $accountUuid: String, $appType: AppType, $statusFilter: ChatStatus) {
    chatsList: getChats(
      userUuid: $userUuid
      accountUuid: $accountUuid
      appType: $appType
      statusFilter: $statusFilter
    ) {
      uuid
      userUuid
      accountUuid
      account {
        uuid
        name
      }
      appType
      status
      title
      summary
      createdAt
      updatedAt
      deletedAt
      lastMessageAt
      hasUnreadMessages
      activeStreamId
    }
  }
`) as TypedDocumentNode<GetChatsByFiltersData, GetChatsByFiltersVariables>;

export const GET_CHAT_WITH_MESSAGES = graphql(`
  query GetChatWithMessages($uuid: String!) {
    getChat(uuid: $uuid) {
      uuid
      userUuid
      accountUuid
      activeStreamId
      title
      summary
      createdAt
      updatedAt
      deletedAt
      hasUnreadMessages
      appType
      account {
        uuid
        name
      }
      messages {
        uuid
        chatUuid
        reaction
        role
        files {
          uuid
          filename
        }
        parts
        metadata
        createdAt
      }
    }
  }
`);

export type GetChatWithMessages = NonNullable<ResultOf<typeof GET_CHAT_WITH_MESSAGES>['getChat']>;
export type GetChatWithMessagesMessage = GetChatWithMessages['messages'][number];

type CanSoftDeleteChatThreadsData = {
  canSoftDeleteChatThreads: boolean;
};

export const CAN_SOFT_DELETE_CHAT_THREADS = graphql(`
  query CanSoftDeleteChatThreads {
    canSoftDeleteChatThreads
  }
`) as TypedDocumentNode<CanSoftDeleteChatThreadsData, Record<string, never>>;

export const GET_ACCOUNT = graphql(`
  query GetAccount($uuid: String!) {
    getAccount(uuid: $uuid) {
      uuid
      name
      programType
    }
  }
`);

export type GetAccount = NonNullable<ResultOf<typeof GET_ACCOUNT>['getAccount']>;

export const GET_SKILLS_BY_FILTERS = graphql(`
  query GetSkillsByFilters($accountUuid: String, $userUuid: String!) {
    skills: getSkillsByFilters(accountUuid: $accountUuid, userUuid: $userUuid) {
      id
      source
      command
      name
      description
      summary
      overview
      category
      enabled
      exampleUsage
      instructionPlaceholder
      accountRequired
    }
  }
`);

export type GetSkillsByFilters = NonNullable<ResultOf<typeof GET_SKILLS_BY_FILTERS>['skills']>;
export type NovaSkill = GetSkillsByFilters[number];

export const GET_CUSTOM_SKILLS = graphql(`
  query GetCustomSkills($filters: CustomSkillFiltersInput) {
    customSkills: getCustomSkills(filters: $filters) {
      uuid
      draftOfCustomSkillUuid
      source
      command
      enabled
      sourceFileUuid
      sourceFileUuids
      frontmatter {
        name
        description
        summary
        overview
        category
      }
      createdAt
      updatedAt
    }
  }
`);

export type GetCustomSkills = NonNullable<ResultOf<typeof GET_CUSTOM_SKILLS>['customSkills']>;

export const GET_SHARED_CUSTOM_SKILLS = graphql(`
  query GetSharedCustomSkills($filters: SharedCustomSkillFiltersInput!) {
    sharedCustomSkills: getSharedCustomSkills(filters: $filters) {
      uuid
      draftOfCustomSkillUuid
      source
      command
      enabled
      sourceFileUuid
      frontmatter {
        name
        description
        summary
        overview
        category
      }
      createdAt
      updatedAt
    }
  }
`);

export type GetSharedCustomSkills = NonNullable<ResultOf<typeof GET_SHARED_CUSTOM_SKILLS>['sharedCustomSkills']>;
export type CustomSkillRecord = GetCustomSkills[number] | GetSharedCustomSkills[number];

export const GET_CUSTOM_SKILL_COLLECTIONS = graphql(`
  query GetCustomSkillCollections(
    $accountUuid: String
    $userUuid: String!
    $customSkillFilters: CustomSkillFiltersInput
    $sharedCustomSkillFilters: SharedCustomSkillFiltersInput!
    $includeCustomSkills: Boolean!
    $includeSharedCustomSkills: Boolean!
  ) {
    runtimeSkills: getSkillsByFilters(accountUuid: $accountUuid, userUuid: $userUuid) {
      id
      source
      command
      name
      description
      summary
      overview
      category
      enabled
      exampleUsage
      instructionPlaceholder
      accountRequired
    }
    customSkills: getCustomSkills(filters: $customSkillFilters) @include(if: $includeCustomSkills) {
      uuid
      draftOfCustomSkillUuid
      source
      command
      enabled
      sourceFileUuid
      sourceFileUuids
      frontmatter {
        name
        description
        summary
        overview
        category
      }
      createdAt
      updatedAt
    }
    sharedCustomSkills: getSharedCustomSkills(filters: $sharedCustomSkillFilters)
      @include(if: $includeSharedCustomSkills) {
      uuid
      draftOfCustomSkillUuid
      source
      command
      enabled
      sourceFileUuid
      sourceFileUuids
      frontmatter {
        name
        description
        summary
        overview
        category
      }
      createdAt
      updatedAt
    }
  }
`);

export const GET_CUSTOM_SKILL_SHARES = graphql(`
  query GetCustomSkillShares($filters: CustomSkillShareFiltersInput!) {
    customSkillShares: getCustomSkillShares(filters: $filters) {
      customSkillUuid
      userUuid
      permission
      createdAt
      updatedAt
    }
  }
`);

export type CustomSkillShareRecord = NonNullable<ResultOf<typeof GET_CUSTOM_SKILL_SHARES>['customSkillShares']>[number];

export const SEARCH_EMPLOYEES = graphql(`
  query SearchEmployees($query: String!, $limit: Int) {
    employees: searchEmployees(query: $query, limit: $limit) {
      userUuid
      email
      firstName
      lastName
      fullName
      imageUrl
      title
    }
  }
`);
